// Regras client-side do wizard de Nova Operação no MODO CEDENTE.
//
// Toda a lógica nova testável do modo cedente vive AQUI (o repo só roda jest
// sobre `lib/*` — lógica em .jsx nasceria órfã de teste).
//
// Duas famílias de regra:
//  (1) GATE DE ENTRADA — decisão 04(a) do owner: toda superfície nova NASCE
//      DESLIGADA. Flag de build + catálogo com ao menos um produto habilitado.
//  (2) MODO REDUZIDO — decisão 01(b) do owner: o cedente NÃO vê taxa nem
//      percentual em NENHUMA superfície. Por isso o erro da V2 NUNCA é exibido
//      cru: ele é traduzido por CÓDIGO para uma copy nossa (o texto da V2 cita
//      taxa/policy). Nunca `JSON.stringify` na tela.

// ── (1) Gate de entrada ──────────────────────────────────────────────────────

// Nome da flag de build (CRA: só variáveis `REACT_APP_*` chegam ao bundle).
// A leitura tem de ser ESTÁTICA (`process.env.REACT_APP_...`) — o webpack
// substitui a expressão inteira em build; acesso dinâmico devolveria undefined.
export const CEDENTE_NEW_OPERATION_FLAG = "REACT_APP_CEDENTE_NEW_OPERATION";

const TRUTHY_FLAG = new Set(["1", "true", "on", "yes", "sim"]);

/**
 * Interpreta o valor cru da flag. FAIL-CLOSED: ausente, vazio, "0", "false"
 * ou qualquer coisa não reconhecida ⇒ false. Só liga com valor explícito.
 */
export function parseFeatureFlag(raw) {
  if (raw == null) return false;
  return TRUTHY_FLAG.has(String(raw).trim().toLowerCase());
}

/** A flag da feature está ligada neste build? (default: NÃO) */
export function isCedenteNewOperationEnabled() {
  return parseFeatureFlag(process.env.REACT_APP_CEDENTE_NEW_OPERATION);
}

/**
 * O catálogo do cedente (`GET /cedente/products` → `{products, bo_unavailable}`)
 * tem ao menos um produto REALMENTE habilitado?
 *
 * `bo_unavailable: true` é o fail-closed do servidor (tudo cinza porque não deu
 * para confirmar) — nunca conta como habilitado. Catálogo ainda não carregado
 * (null) também não: enquanto não se sabe, a entrada não aparece.
 */
export function catalogHasEnabledProduct(catalog) {
  if (!catalog || typeof catalog !== "object") return false;
  if (catalog.bo_unavailable === true) return false;
  const list = Array.isArray(catalog.products) ? catalog.products : [];
  return list.some((p) => p && p.enabled === true);
}

/**
 * A ENTRADA (botão/menu/deep-link) para a nova operação deve renderizar?
 * Exige as DUAS condições: flag ligada E catálogo com produto habilitado.
 * Qualquer dúvida ⇒ false (nada renderiza).
 */
export function cedenteNewOperationVisible({ flagOn, catalog } = {}) {
  return flagOn === true && catalogHasEnabledProduct(catalog);
}

// ── (2) Erros: código → copy do cedente ──────────────────────────────────────

// Copy única do "não é você, é configuração" (os 3 códigos terminais caem
// aqui). Decisões 09.3 + 09.5 do owner: NÃO citar terceiro ("seu originador" —
// o cedente auto-registrado não conhece esse ator) e NÃO apontar canal de
// suporte, porque não existe canal nas telas do cedente e a frase mandaria o
// usuário para o vazio. Quando o canal existir (ROADMAP do hub), é UMA string.
export const CEDENTE_TERMINAL_COPY =
  "As condições deste produto ainda não foram configuradas para o seu cadastro. " +
  "Enquanto isso, não é possível criar a operação com ele.";

// 422 que não deveria acontecer no ramo cedente (o front nunca envia preço):
// se aparecer, é bug NOSSO — não frasear como erro do usuário.
export const CEDENTE_INTERNAL_COPY =
  "Não foi possível concluir esta operação por uma inconsistência interna. " +
  "Nada foi criado — tente novamente em instantes.";

// Erro transitório (503, rede fora): a ação continua possível.
export const CEDENTE_TRANSIENT_COPY =
  "Serviço temporariamente indisponível. Tente novamente em instantes.";

// Código desconhecido / detail fora de contrato: copy genérica. NUNCA
// `JSON.stringify` do erro na tela.
export const CEDENTE_ERROR_FALLBACK =
  "Não foi possível concluir esta operação agora. Revise os dados dos recebíveis " +
  "e tente novamente.";

// Rejeição de policy (bloco consultivo `policy_verdict` da simulação): copy
// GENÉRICA — o `reason` da V2 cita a faixa de taxa e não pode ser exibido.
export const CEDENTE_POLICY_REJECT_COPY =
  "As condições do seu cadastro não permitem esta operação com estes valores. " +
  "Ajuste os valores ou prazos dos recebíveis.";

// Cliente do deep-link que não existe mais / não está na lista.
export const CLIENTE_INDISPONIVEL_COPY = "Cliente indisponível — selecione outro.";

// ── Erros de CONTA/CADASTRO (409/503 do próprio Portal) ─────────────────────
// Não têm nada a ver com os recebíveis: mandar "revise os recebíveis" aqui é
// mandar o usuário consertar o que não está quebrado.

export const CEDENTE_SEM_ENTIDADE_COPY =
  "O seu cadastro ainda está em aprovação. A criação de operações fica " +
  "disponível assim que ele for aprovado.";

export const CEDENTE_REGENTE_NAO_RESOLVIDO_COPY =
  "O seu cadastro ainda não está configurado para criar operações. " +
  "Assim que a configuração concluir, esta tela fica disponível.";

export const CEDENTE_CADASTRO_INDISPONIVEL_COPY =
  "Não foi possível confirmar os dados da sua conta agora. Tente novamente em instantes.";

export const CEDENTE_SEM_SINCRONIA_COPY =
  "O seu cadastro ainda não terminou de ser sincronizado. A criação de " +
  "operações fica disponível quando a sincronização concluir.";

export const CEDENTE_RATE_LIMIT_COPY =
  "Muitas tentativas — aguarde alguns segundos e tente novamente.";

// `CEDENTE_SEM_ASSIGNOR_DETAIL` (`cedente_operations.py:1009`) chega em PROSA,
// sem prefixo de código. Casamos por um trecho ESTÁVEL do texto — não pela
// frase inteira, que muda a cada revisão de copy do backend.
export const SEM_ASSIGNOR_MATCH = "sincronizado com o sistema central";

// Códigos 422 TERMINAIS de PREÇO: nenhuma alteração de valor/prazo resolve —
// bloqueiam o avanço já no passo Recebíveis (fonte: EXEC-A §R2.7, na V2).
export const CEDENTE_TERMINAL_CODES = Object.freeze([
  "fee_not_configured",
  "no_policy_resolvable",
  "fee_invalid_value",
]);

// Códigos 409 de CONTA: também bloqueiam, mas a copy fala de CADASTRO.
export const CEDENTE_ACCOUNT_CODES = Object.freeze([
  "cedente_sem_entidade_bo",
  "regente_nao_resolvido",
]);

/**
 * Escopo do erro — decide QUAL apoio o card de bloqueio mostra.
 * `pricing` ⇒ "tente outro produto"; `account` ⇒ NADA sobre recebíveis/produto
 * (não há o que o usuário mexa no wizard); `generic` ⇒ sem apoio.
 */
export const SCOPE_PRICING = "pricing";
export const SCOPE_ACCOUNT = "account";
export const SCOPE_GENERIC = "generic";

// Mapa fechado código → copy. `standard_rate_required` está no mapa (é o 4º
// código da família) mas NÃO é terminal: ele só aparece se alguém reintroduzir
// campo de preço no corpo, e aí a copy é "erro interno", não "fale com o BO".
export const CEDENTE_ERROR_COPY = Object.freeze({
  fee_not_configured: CEDENTE_TERMINAL_COPY,
  no_policy_resolvable: CEDENTE_TERMINAL_COPY,
  fee_invalid_value: CEDENTE_TERMINAL_COPY,
  standard_rate_required: CEDENTE_INTERNAL_COPY,
  cedente_sem_entidade_bo: CEDENTE_SEM_ENTIDADE_COPY,
  regente_nao_resolvido: CEDENTE_REGENTE_NAO_RESOLVIDO_COPY,
  regente_indisponivel: CEDENTE_CADASTRO_INDISPONIVEL_COPY,
});

// Códigos cuja copy já diz "tente novamente" — transitórios por natureza.
const CEDENTE_TRANSIENT_CODES = Object.freeze(["regente_indisponivel"]);

// Erros de ASSINANTE da V2 (mesmo conjunto do `SIGNER_ERROR_RE` do wizard):
// a mensagem já vem acionável, em PT, e NÃO cita taxa/percentual — é a única
// família que passa INTACTA para a tela no modo cedente (o wizard depende dela
// para mandar o usuário de volta ao passo Assinantes com o motivo certo).
export const SIGNER_PASSTHROUGH_RE =
  /signers_required|invalid_signer|too_many_signers|signer_incomplete_for_contract|signers_incomplete/;

/**
 * Extrai o CÓDIGO do `detail` de erro.
 *
 * Os 422 terminais da V2 chegam com `detail` STRING e prefixo snake_case até o
 * primeiro `:` — ex.: "fee_not_configured: a policy resolvida ...". Já o
 * `CET_OUT_OF_BOUNDS` chega como DICT (`{code, min_pct, max_pct, ...}`): as
 * duas formas são cobertas. Array (422 de validação pydantic) e qualquer outra
 * coisa ⇒ null (cai no fallback genérico).
 */
export function errorCodeFromDetail(detail) {
  if (typeof detail === "string") {
    const head = detail.split(":")[0].trim();
    return /^[a-z][a-z0-9_]*$/.test(head) ? head : null;
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return typeof detail.code === "string" && detail.code.trim() ? detail.code.trim() : null;
  }
  return null;
}

/**
 * Traduz um erro de API para o modo cedente.
 *
 * @param detail  `error.response.data.detail` (string, objeto ou array)
 * @param status  HTTP status (opcional). 503/502/504 e ausência de resposta
 *                (rede fora) ⇒ transitório.
 * @returns {{code, message, terminal, transient, scope, rateLimited}}
 *
 * - `terminal: true` ⇒ o passo Recebíveis bloqueia (nenhum ajuste do usuário
 *   resolve; deixar seguir só levaria a um botão cinza na Revisão).
 * - `transient: true` ⇒ "tente novamente".
 * - `scope` decide a copy de APOIO: preço vs. cadastro (ver SCOPE_*).
 * - `rateLimited: true` (429) ⇒ o chamador deve espaçar a próxima tentativa.
 * - Código desconhecido ⇒ copy genérica; NUNCA o texto cru da V2 (cita taxa)
 *   e NUNCA `JSON.stringify`.
 */
export function cedenteErrorInfo(detail, status) {
  const code = errorCodeFromDetail(detail);
  const transientStatus = status === 502 || status === 503 || status === 504 || status === 0;

  // 429 vem ANTES do mapa: o rate-limit é do Portal e não diz nada sobre o
  // conteúdo — mandar "fale com o suporte" aqui seria mentira.
  if (status === 429) {
    return {
      code, message: CEDENTE_RATE_LIMIT_COPY, terminal: false,
      transient: true, scope: SCOPE_GENERIC, rateLimited: true,
    };
  }
  if (code && Object.prototype.hasOwnProperty.call(CEDENTE_ERROR_COPY, code)) {
    const isAccount = CEDENTE_ACCOUNT_CODES.includes(code) || CEDENTE_TRANSIENT_CODES.includes(code);
    return {
      code,
      message: CEDENTE_ERROR_COPY[code],
      terminal: CEDENTE_TERMINAL_CODES.includes(code) || CEDENTE_ACCOUNT_CODES.includes(code),
      transient: CEDENTE_TRANSIENT_CODES.includes(code),
      scope: isAccount ? SCOPE_ACCOUNT : SCOPE_PRICING,
      rateLimited: false,
    };
  }
  // "Cedente ainda não sincronizado com o sistema central" — prosa sem código.
  if (typeof detail === "string" && detail.includes(SEM_ASSIGNOR_MATCH)) {
    return {
      code, message: CEDENTE_SEM_SINCRONIA_COPY, terminal: true,
      transient: false, scope: SCOPE_ACCOUNT, rateLimited: false,
    };
  }
  if (transientStatus) {
    return {
      code, message: CEDENTE_TRANSIENT_COPY, terminal: false,
      transient: true, scope: SCOPE_GENERIC, rateLimited: false,
    };
  }
  // Família de assinantes: mensagem da V2 passa intacta (acionável, sem preço).
  if (typeof detail === "string" && SIGNER_PASSTHROUGH_RE.test(detail)) {
    return {
      code, message: detail, terminal: false, transient: false,
      scope: SCOPE_GENERIC, rateLimited: false,
    };
  }
  return {
    code, message: CEDENTE_ERROR_FALLBACK, terminal: false, transient: false,
    scope: SCOPE_GENERIC, rateLimited: false,
  };
}

// Espera adicional no debounce da simulação depois de um 429 (o limite do
// backend é por minuto: `60/minute` no simulate). Um valor só, previsível.
export const RATE_LIMIT_BACKOFF_MS = 5000;

/**
 * Quanto esperar antes da PRÓXIMA simulação automática.
 * Sem 429 recente ⇒ o debounce normal; com 429 ⇒ debounce + backoff.
 */
export function simulationDelayMs(baseMs, rateLimited) {
  return rateLimited ? baseMs + RATE_LIMIT_BACKOFF_MS : baseMs;
}

/** Açúcar: só a copy. */
export function cedenteErrorMessage(detail, status) {
  return cedenteErrorInfo(detail, status).message;
}

// ── Money-path: valor líquido presente e exibível ────────────────────────────

/**
 * Há um valor líquido REAL para mostrar (e para o cedente confirmar em cima)?
 *
 * A V2 devolve os valores como STRING — `!!v` daria `true` para `"0.00"`, que é
 * exatamente o "R$ 0,00 fabricado" que não pode aparecer. Exige número finito e
 * **positivo**: o cedente nunca confirma uma operação sem ver quanto recebe.
 */
export function hasVisibleLiquidValue(v) {
  if (v == null || v === "") return false;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) && n > 0;
}

// ── Catálogo cinza: motivo → copy (campo `unavailable_reason`, round 3) ──────

export const CATALOGO_MOTIVO_COPY = Object.freeze({
  no_regent:
    "O seu cadastro ainda não está configurado para operar — por isso nenhum " +
    "produto aparece disponível.",
  bo_down:
    "Não foi possível confirmar agora quais produtos estão disponíveis para você. " +
    "Tente novamente em instantes.",
  internal:
    "Não foi possível carregar os produtos disponíveis agora. Tente novamente em instantes.",
});

/** Copy do catálogo cinza. Motivo ausente/desconhecido ⇒ a genérica de sempre. */
export function catalogoIndisponivelCopy(reason, fallback) {
  return CATALOGO_MOTIVO_COPY[reason] || fallback;
}

// ── Load inicial: dizer O QUE falhou, não só "falhou" ────────────────────────

export const LOAD_PARTS = Object.freeze({
  products: "os produtos",
  me: "os dados da sua empresa",
  clientes: "os seus clientes",
});

/**
 * "Não foi possível carregar os produtos e os seus clientes." — a causa
 * separada, para o usuário saber se o problema é geral ou de uma superfície só.
 */
export function loadFailureCopy(parts, labels) {
  const dicionario = labels ? { ...LOAD_PARTS, ...labels } : LOAD_PARTS;
  const nomes = (Array.isArray(parts) ? parts : [])
    .map((p) => dicionario[p])
    .filter(Boolean);
  if (nomes.length === 0) return "Não foi possível carregar os dados desta tela.";
  const lista = nomes.length === 1
    ? nomes[0]
    : `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`;
  return `Não foi possível carregar ${lista}.`;
}

// ── Telefone: máscara ÚNICA (havia 2 cópias — dialog inline e página) ────────

/**
 * Máscara de telefone do cadastro de cliente. Cópia VERBATIM da que estava em
 * `pages/cedente/Clientes.jsx` (a página e o dialog inline gravam o MESMO
 * campo — duas máscaras divergindo produziriam formatos diferentes na tabela).
 */
export function maskPhoneCedente(v) {
  const d = String(v || "").replace(/\D/g, "");
  if (d.length <= 2) return `+${d}`;
  if (d.length <= 4) return `+${d.slice(0, 2)} ${d.slice(2)}`;
  if (d.length <= 8) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4)}`;
  if (d.length <= 9) return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4)}`;
  return `+${d.slice(0, 2)} ${d.slice(2, 4)} ${d.slice(4, 9)}-${d.slice(9, 13)}`;
}

/** Açúcar: o erro bloqueia o passo Recebíveis? */
export function isTerminalCedenteError(detail, status) {
  return cedenteErrorInfo(detail, status).terminal;
}

// ── (3) Sacado = cliente do cedente ──────────────────────────────────────────

/**
 * Cliente (`/cedente/clientes`) → campos de sacado do recebível. O payload
 * enviado à V2 fica INALTERADO (`payer_name` / `payer_document`): a seleção só
 * substitui a digitação livre do modo parceiro.
 */
export function clienteToPayer(cliente) {
  return {
    payer_name: cliente?.razao_social || "",
    payer_document: cliente?.cnpj || "",
  };
}

/**
 * URL da lista de clientes. Espelha o precedente do wizard do parceiro
 * (`NewOperation.jsx:98-99`): com pré-seleção por deep-link NÃO se fixa
 * `?status=active` — senão um cliente inativo pré-selecionado sumiria da lista
 * e o wizard mostraria "indisponível" para um cadastro que existe.
 */
export function clientesUrlForDeepLink(clienteId) {
  return clienteId ? "/cedente/clientes" : "/cedente/clientes?status=active";
}

/**
 * Resolve o `?cliente_id=` do deep-link contra a lista carregada.
 * @returns {{cliente: object|null, unresolved: boolean}} — `unresolved` só é
 * true quando VEIO um id e ele não casou (sem id não há nada a resolver).
 */
export function resolveDeepLinkCliente(clientes, clienteId) {
  if (!clienteId) return { cliente: null, unresolved: false };
  const list = Array.isArray(clientes) ? clientes : [];
  const cliente = list.find((c) => c && c.id === clienteId) || null;
  return { cliente, unresolved: !cliente };
}

// ── (3b) Cadastro inline de cliente (Q5 do owner — decidida 2026-07-31) ──────
//
// O dialog inline segue o padrão do `RepresentativeFormDialog`: dialog → POST →
// recarrega a lista → seleciona o recém-criado NA LINHA que originou o clique.
// A validação client-side é ESPELHO do `CedenteCreateClientePayload`
// (`backend/app/models.py:84-89`): razao_social e cnpj obrigatórios; telefone e
// e-mail opcionais; `limite_credito` default 0. O servidor é a autoridade
// (409 de CNPJ duplicado, 422 de e-mail inválido) — aqui é só feedback imediato.

/** Rótulo da superfície de sacado (decisão literal do owner): "clientes do
 *  cedente SÃO os sacados da operação". `(sacados)` vai em texto secundário. */
export const SACADO_LABEL = "Clientes";
export const SACADO_LABEL_SUFFIX = "(sacados)";

/** Só os dígitos. */
export function onlyDigitsDoc(v) {
  return String(v || "").replace(/\D/g, "");
}

/**
 * Máscara de CNPJ. Cópia VERBATIM da que já existia em
 * `pages/cedente/Clientes.jsx` — a página passou a importar daqui para não
 * ficarem duas máscaras divergindo sobre o MESMO cadastro.
 */
export function maskCNPJ(v) {
  return String(v || "").replace(/\D/g, "")
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2")
    .slice(0, 18);
}

/**
 * CNPJ como o backend grava. Equivalente VERBATIM à expressão que estava
 * inline em `pages/cedente/Clientes.jsx:129` (dígitos → "00.000.000/0000-00"):
 * a página passou a chamar esta função, então os dois caminhos de cadastro
 * gravam o documento no MESMO formato. Comprimento ≠ 14 ⇒ só os dígitos (o
 * regex não casa) — comportamento idêntico ao anterior, sem inventar formato.
 */
export function formatCnpjPayload(v) {
  return onlyDigitsDoc(v).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
}

// Espelho do EmailStr do pydantic — só o suficiente para o feedback imediato.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Validação do form de cliente inline. Devolve `{campo: mensagem}` — objeto
 * VAZIO significa "pode enviar". Espelha o schema do servidor; nunca substitui.
 */
export function validateClienteForm(form) {
  const errors = {};
  if (!String(form?.razao_social || "").trim()) {
    errors.razao_social = "Informe a razão social do cliente.";
  }
  const digits = onlyDigitsDoc(form?.cnpj);
  if (!digits) errors.cnpj = "Informe o CNPJ do cliente.";
  else if (digits.length !== 14) errors.cnpj = "CNPJ deve ter 14 dígitos.";
  const email = String(form?.contato_email || "").trim();
  if (email && !EMAIL_RE.test(email)) errors.contato_email = "E-mail inválido.";
  return errors;
}

/**
 * Form → corpo do `POST /cedente/clientes`. Paridade EXATA com o cadastro da
 * página de Clientes: os dois caminhos criam a mesma linha.
 */
export function clienteCreatePayload(form) {
  return {
    razao_social: String(form?.razao_social || "").trim(),
    cnpj: formatCnpjPayload(form?.cnpj),
    contato_telefone: String(form?.contato_telefone || "").trim(),
    contato_email: String(form?.contato_email || "").trim() || null,
    limite_credito: 0,
  };
}

/**
 * Qual id selecionar na linha depois de criar o cliente?
 *
 * Caminho normal: o id que o POST devolveu, confirmado na lista recarregada.
 * Defensivo: se a lista recarregada não trouxer aquele id (filtro de status,
 * corrida de replicação), casa por CNPJ em dígitos. Nada casou ⇒ `null` —
 * melhor não selecionar nada do que selecionar o cliente ERRADO.
 */
export function pickCreatedCliente(clientes, created) {
  const list = Array.isArray(clientes) ? clientes : [];
  if (!created) return null;
  if (created.id && list.some((c) => c && c.id === created.id)) return created.id;
  const digits = onlyDigitsDoc(created.cnpj);
  if (!digits) return null;
  const byDoc = list.find((c) => c && onlyDigitsDoc(c.cnpj) === digits);
  return byDoc ? byDoc.id : null;
}

// ── (4) Copy derivada ────────────────────────────────────────────────────────

/**
 * "em 4 passos" / "em 5 passos" — derivado do fluxo REAL (PJ tem um passo a
 * mais que PF, e o modo cedente tem um a menos que o do parceiro). Nunca
 * hardcode: o subtítulo mentia para o cedente PJ do parceiro.
 */
export function passosLabel(totalSteps) {
  const n = Number.isFinite(totalSteps) && totalSteps > 0 ? Math.trunc(totalSteps) : 0;
  if (n <= 0) return "";
  return n === 1 ? "em 1 passo" : `em ${n} passos`;
}
